import { useCallback, useEffect, useState } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts'
import {
  Building2,
  LayoutGrid,
  Cpu,
  Users,
  Radio,
  Timer,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  ShieldAlert,
  Wifi,
} from 'lucide-react'
import { usePlatformFilterStore } from '../../../store/platformFilterStore'
import { platformOrgService } from '../../../services/platformOrgService'
import { platformAnalyticsService } from '../../../services/platformAnalyticsService'
import type { PlatformOverviewResponse } from '../../../types/api.types'
import type {
  PlatformUsageResponse,
  OrgDetailUsageResponse,
  TeamUsageSummary,
  OrgUsageSummary,
  IncidentAnalyticsResponse,
  DeviceHealthResponse,
  ConsumptionResponse,
} from '../../../types/api.types'

// ── Helpers ──

function today(): string { return new Date().toISOString().slice(0, 10) }
function daysAgo(n: number): string {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10)
}
function completionPct(completed: number, total: number): string {
  if (!total) return '—'
  return `${((completed / total) * 100).toFixed(1)}%`
}
function fmtDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

// ── Reusable small components ──

function TrendIcon({ trend }: { trend?: 'up' | 'down' | 'neutral' }) {
  if (trend === 'up') return <TrendingUp size={11} className="inline text-green-500" />
  if (trend === 'down') return <TrendingDown size={11} className="inline text-red-500" />
  return <Minus size={11} className="inline text-gray-400" />
}

function StatCard({
  label, value, sub, subTrend, Icon, iconBg, iconColor,
}: {
  label: string; value: string | number; sub: string; subTrend?: 'up' | 'down' | 'neutral'
  Icon: React.ElementType; iconBg: string; iconColor: string
}): JSX.Element {
  return (
    <div className="flex items-start justify-between rounded-xl border border-gray-200 bg-white p-5">
      <div className="min-w-0">
        <p className="text-[12px] text-gray-500">{label}</p>
        <p className="mt-1 truncate text-[26px] font-bold leading-tight text-gray-900">{value}</p>
        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500">
          <TrendIcon trend={subTrend} />
          {sub}
        </p>
      </div>
      <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${iconBg}`}>
        <Icon size={17} className={iconColor} />
      </div>
    </div>
  )
}

// ── Org Row (expandable) ──
function OrgRow({
  org, rank, onDrillDown,
}: { org: OrgUsageSummary; rank: number; onDrillDown: (orgId: number) => void }): JSX.Element {
  const [open, setOpen] = useState(false)
  const completion = completionPct(org.completed_calls ?? 0, org.total_calls ?? 0)

  return (
    <>
      <tr
        className="cursor-pointer border-t border-gray-100 hover:bg-gray-50/60"
        onClick={() => setOpen(v => !v)}
      >
        <td className="px-4 py-3 text-[13px] text-gray-400">#{rank}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
            <span className="text-[13px] font-medium text-gray-900">{org.organization_name}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-right text-[13px] font-semibold text-gray-900">{fmtDuration(org.total_duration_seconds ?? 0)}</td>
        <td className="px-4 py-3 text-right text-[13px] text-gray-700">{(org.total_calls ?? 0).toLocaleString()}</td>
        <td className="px-4 py-3 text-right text-[13px] text-gray-700">{org.property_count}</td>
        <td className="px-4 py-3 text-right text-[13px]">
          <span className={`font-medium ${Number(completion.replace('%', '')) >= 95 ? 'text-green-600' : 'text-amber-600'}`}>
            {completion}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDrillDown(org.organization_id) }}
            className="rounded px-2 py-0.5 text-[11px] font-medium text-brand-green hover:bg-brand-green/10"
          >
            Details
          </button>
        </td>
      </tr>
      {open && (org.properties ?? []).map((prop) => (
        <tr key={prop.property_id} className="border-t border-gray-50 bg-gray-50/40">
          <td className="px-4 py-2.5" />
          <td className="px-4 py-2.5 pl-10 text-[12px] text-gray-700">{prop.property_name} <span className="text-gray-400">({prop.property_code})</span></td>
          <td className="px-4 py-2.5 text-right text-[12px] text-gray-600">{fmtDuration(prop.total_duration_seconds ?? 0)}</td>
          <td className="px-4 py-2.5 text-right text-[12px] text-gray-600">{(prop.total_calls ?? 0).toLocaleString()}</td>
          <td className="px-4 py-2.5 text-right text-[12px] text-gray-500">{prop.device_count} devices</td>
          <td className="px-4 py-2.5 text-right text-[12px] text-gray-500">
            {completionPct(prop.completed_calls ?? 0, prop.total_calls ?? 0)}
          </td>
          <td />
        </tr>
      ))}
    </>
  )
}

// ── Main page ──
export function PlatformLiveOpsPage(): JSX.Element {
  const { organizations, allProperties } = usePlatformFilterStore()
  const selectedOrgId = usePlatformFilterStore((s) => s.selectedOrgId)

  // Overview stats from dedicated endpoint
  const [overview, setOverview] = useState<PlatformOverviewResponse | null>(null)

  // Analytics state
  const [fromDate, setFromDate] = useState(daysAgo(7))
  const [toDate, setToDate] = useState(today())
  const [pendingFrom, setPendingFrom] = useState(daysAgo(7))
  const [pendingTo, setPendingTo] = useState(today())

  const [usage, setUsage] = useState<PlatformUsageResponse | null>(null)
  const [orgDetail, setOrgDetail] = useState<OrgDetailUsageResponse | null>(null)
  const [topTeams, setTopTeams] = useState<TeamUsageSummary[]>([])
  const [drillOrgId, setDrillOrgId] = useState<number | null>(null)
  const [incidents, setIncidents] = useState<IncidentAnalyticsResponse | null>(null)
  const [deviceHealth, setDeviceHealth] = useState<DeviceHealthResponse | null>(null)
  const [consumption, setConsumption] = useState<ConsumptionResponse | null>(null)
  const [consumptionYear, setConsumptionYear] = useState(() => new Date().getFullYear())
  const [consumptionMonth, setConsumptionMonth] = useState(() => new Date().getMonth() + 1)

  const [usageLoading, setUsageLoading] = useState(false)
  const [usageError, setUsageError] = useState(false)
  const [teamsLoading, setTeamsLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [incidentsLoading, setIncidentsLoading] = useState(false)
  const [deviceHealthLoading, setDeviceHealthLoading] = useState(false)
  const [consumptionLoading, setConsumptionLoading] = useState(false)

  // Fetch platform overview once
  useEffect(() => {
    platformOrgService.getOverview().then(setOverview).catch(() => { /* non-critical */ })
  }, [])

  // Fetch analytics (usage + teams) when date range is applied
  const loadAnalytics = useCallback(async (from: string, to: string, orgIdFilter?: number | null) => {
    setUsageLoading(true); setUsageError(false)
    setTeamsLoading(true); setIncidentsLoading(true)
    const [usageResult, teamsResult, incidentsResult] = await Promise.allSettled([
      platformAnalyticsService.getUsage(from, to),
      platformAnalyticsService.getTopTeams(from, to, orgIdFilter ?? undefined, 10),
      platformAnalyticsService.getIncidents(from, to, orgIdFilter ?? undefined),
    ])
    if (usageResult.status === 'fulfilled') setUsage(usageResult.value)
    else setUsageError(true)
    if (teamsResult.status === 'fulfilled') {
      const raw = teamsResult.value
      setTopTeams(Array.isArray(raw) ? raw : [])
    }
    setIncidents(incidentsResult.status === 'fulfilled' ? incidentsResult.value : null)
    setUsageLoading(false); setTeamsLoading(false); setIncidentsLoading(false)
  }, [])

  const loadDeviceHealth = useCallback(async () => {
    setDeviceHealthLoading(true)
    try { setDeviceHealth(await platformAnalyticsService.getDeviceHealth()) }
    catch { setDeviceHealth(null) }
    finally { setDeviceHealthLoading(false) }
  }, [])

  const loadConsumption = useCallback(async (year: number, month: number) => {
    setConsumptionLoading(true)
    try { setConsumption(await platformAnalyticsService.getConsumption(year, month)) }
    catch { setConsumption(null) }
    finally { setConsumptionLoading(false) }
  }, [])

  // Initial load + reload when topbar org filter changes
  useEffect(() => {
    loadAnalytics(fromDate, toDate, selectedOrgId)
  }, [fromDate, toDate, selectedOrgId, loadAnalytics])

  // Device health — real-time, platform-wide
  useEffect(() => { loadDeviceHealth() }, [loadDeviceHealth])

  // Monthly consumption
  useEffect(() => { loadConsumption(consumptionYear, consumptionMonth) }, [consumptionYear, consumptionMonth, loadConsumption])

  // Load org detail when drillOrgId changes
  useEffect(() => {
    const targetId = drillOrgId ?? selectedOrgId
    if (!targetId) { setOrgDetail(null); return }
    setDetailLoading(true)
    platformAnalyticsService
      .getOrgUsage(targetId, fromDate, toDate)
      .then(setOrgDetail)
      .catch(() => setOrgDetail(null))
      .finally(() => setDetailLoading(false))
  }, [drillOrgId, selectedOrgId, fromDate, toDate])

  function applyDates() {
    setFromDate(pendingFrom)
    setToDate(pendingTo)
  }

  // Derived analytics summary
  const totalCalls = usage?.total_calls ?? 0
  const totalSeconds = usage?.total_duration_seconds ?? 0
  const completedCalls = usage?.organizations.reduce((s, o) => s + (o.completed_calls ?? 0), 0) ?? 0
  const globalCompletion = totalCalls ? ((completedCalls / totalCalls) * 100).toFixed(1) : '—'

  // Show orgs filtered by topbar org selection if set
  const displayOrgs = selectedOrgId
    ? (usage?.organizations.filter((o) => o.organization_id === selectedOrgId) ?? [])
    : (usage?.organizations ?? [])

  const drillOrg = orgDetail
    ? orgDetail
    : null

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Live Ops</h1>

      {/* ── Infrastructure stats ── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Total Organisations" value={overview?.total_organizations ?? (organizations.length || '—')} sub={`${organizations.filter(o => o.is_active).length} active`} subTrend="up" Icon={Building2} iconBg="bg-blue-50" iconColor="text-blue-400" />
        <StatCard label="Total Properties" value={overview?.total_properties ?? (allProperties.length || '—')} sub="across all orgs" subTrend="neutral" Icon={LayoutGrid} iconBg="bg-purple-50" iconColor="text-purple-400" />
        <StatCard label="Total Devices" value={overview?.total_devices ?? '—'} sub="registered devices" subTrend="neutral" Icon={Cpu} iconBg="bg-green-50" iconColor="text-green-500" />
        <StatCard label="Total Users" value={overview?.total_users ?? '—'} sub="across all orgs" subTrend="up" Icon={Users} iconBg="bg-indigo-50" iconColor="text-indigo-400" />
      </div>

      {/* ── Analytics section ── */}
      <div className="rounded-xl border border-gray-200 bg-white">
        {/* Header + date picker */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
          <h2 className="text-[16px] font-bold text-gray-900">Communication Analytics</h2>
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-gray-500">From</span>
            <input
              type="date"
              value={pendingFrom}
              max={pendingTo}
              onChange={(e) => setPendingFrom(e.target.value)}
              className="h-8 rounded-lg border border-gray-200 px-2 text-[13px] text-gray-700 outline-none focus:border-brand-green"
            />
            <span className="text-[12px] text-gray-500">To</span>
            <input
              type="date"
              value={pendingTo}
              min={pendingFrom}
              max={today()}
              onChange={(e) => setPendingTo(e.target.value)}
              className="h-8 rounded-lg border border-gray-200 px-2 text-[13px] text-gray-700 outline-none focus:border-brand-green"
            />
            <button
              type="button"
              onClick={applyDates}
              disabled={usageLoading}
              className="flex h-8 items-center gap-1.5 rounded-lg bg-brand-green px-3 text-[13px] font-semibold text-black hover:opacity-80 disabled:opacity-50"
            >
              <RefreshCw size={13} className={usageLoading ? 'animate-spin' : ''} />
              Apply
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Analytics summary cards */}
          {usageError && (
            <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-700">
              <span>Could not reach the analytics API — data below may be unavailable.</span>
              <button
                type="button"
                onClick={() => loadAnalytics(fromDate, toDate, selectedOrgId)}
                className="ml-4 rounded px-2 py-0.5 text-[12px] font-semibold hover:bg-amber-100"
              >
                Retry
              </button>
            </div>
          )}

          <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 text-[12px] text-gray-500"><Radio size={14} />Total Transmissions</div>
                  <p className="mt-1.5 text-[24px] font-bold text-gray-900">
                    {usageLoading ? '...' : totalCalls.toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 text-[12px] text-gray-500"><Timer size={14} />Total Audio Time</div>
                  <p className="mt-1.5 text-[24px] font-bold text-gray-900">
                    {usageLoading ? '...' : fmtDuration(totalSeconds)}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 text-[12px] text-gray-500"><CheckCircle size={14} />Completion Rate</div>
                  <p className={`mt-1.5 text-[24px] font-bold ${Number(globalCompletion) >= 95 ? 'text-green-600' : 'text-amber-600'}`}>
                    {usageLoading ? '...' : `${globalCompletion}%`}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
                  <div className="flex items-center gap-2 text-[12px] text-gray-500"><TrendingUp size={14} />Orgs Reporting</div>
                  <p className="mt-1.5 text-[24px] font-bold text-gray-900">
                    {usageLoading ? '...' : (usage?.organizations.length ?? '—')}
                  </p>
                </div>
              </div>

              {/* Top Organisations table */}
              <div>
                <h3 className="mb-3 text-[14px] font-semibold text-gray-800">
                  Top Organisations by Usage
                  {selectedOrgId && <span className="ml-2 text-[12px] font-normal text-brand-green">(filtered by topbar)</span>}
                </h3>
                {usageLoading ? (
                  <p className="py-8 text-center text-[13px] text-gray-400">Loading...</p>
                ) : displayOrgs.length === 0 ? (
                  <p className="py-8 text-center text-[13px] text-gray-400">No usage data for this period.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full min-w-[640px]">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">#</th>
                          <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Organisation</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Audio Time</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Transmissions</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Properties</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Completion</th>
                          <th className="px-4 py-2.5 text-center text-[11px] font-medium uppercase tracking-wide text-gray-500">Detail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayOrgs.map((org, i) => (
                          <OrgRow
                            key={org.organization_id}
                            org={org}
                            rank={i + 1}
                            onDrillDown={(id) => setDrillOrgId(prev => prev === id ? null : id)}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Org Detail — daily breakdown chart */}
              {(drillOrgId ?? selectedOrgId) && (
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-[14px] font-semibold text-gray-800">
                      Daily Breakdown — {detailLoading ? '...' : drillOrg?.organization_name}
                    </h3>
                    {drillOrgId && (
                      <button type="button" onClick={() => setDrillOrgId(null)} className="text-[12px] text-gray-400 hover:text-gray-600">
                        Clear drill-down ×
                      </button>
                    )}
                  </div>
                  {detailLoading ? (
                    <p className="py-8 text-center text-[13px] text-gray-400">Loading org detail...</p>
                  ) : drillOrg ? (
                    <>
                      {/* Org summary pills */}
                      <div className="mb-4 flex flex-wrap gap-3 text-[12px]">
                        {[
                          ['Transmissions', (drillOrg.total_calls ?? 0).toLocaleString()],
                          ['Completed', (drillOrg.completed_calls ?? 0).toLocaleString()],
                          ['Audio Time', fmtDuration(drillOrg.total_duration_seconds ?? 0)],
                          ['Avg Duration', fmtDuration(drillOrg.avg_call_duration_seconds ?? 0)],
                          ['Completion', completionPct(drillOrg.completed_calls ?? 0, drillOrg.total_calls ?? 0)],
                        ].map(([label, val]) => (
                          <span key={label} className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-gray-700">
                            <span className="font-semibold">{val}</span> <span className="text-gray-400">{label}</span>
                          </span>
                        ))}
                      </div>

                      {/* Daily calls bar chart */}
                      <ResponsiveContainer width="100%" height={180}>
                        <BarChart data={drillOrg.daily_breakdown} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                          <XAxis
                            dataKey="date"
                            tick={{ fontSize: 10, fill: '#9ca3af' }}
                            tickFormatter={(d) => d.slice(5)}
                            interval="preserveStartEnd"
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                          <Tooltip
                            formatter={(val) => [Number(val).toLocaleString(), 'Transmissions']}
                            contentStyle={{ border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}
                          />
                          <Bar dataKey="total_calls" fill="#a3c93a" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </>
                  ) : null}
                </div>
              )}

              {/* Top Teams table */}
              <div>
                <h3 className="mb-3 text-[14px] font-semibold text-gray-800">Top Teams by Transmission Time</h3>
                {teamsLoading ? (
                  <p className="py-8 text-center text-[13px] text-gray-400">Loading teams...</p>
                ) : topTeams.length === 0 ? (
                  <p className="py-8 text-center text-[13px] text-gray-400">No team data for this period.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full min-w-[640px]">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Team</th>
                          <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Organisation</th>
                          <th className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">Property</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Audio Time</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Transmissions</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Completion</th>
                          <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Devices</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topTeams.map((team) => (
                          <tr key={team.team_id} className="border-t border-gray-100 hover:bg-gray-50/50">
                            <td className="px-4 py-3">
                              <p className="text-[13px] font-medium text-gray-900">{team.team_name}</p>
                              {team.team_type && <p className="text-[11px] text-gray-400">{team.team_type}</p>}
                            </td>
                            <td className="px-4 py-3 text-[13px] text-gray-600">{team.organization_name}</td>
                            <td className="px-4 py-3 text-[13px] text-gray-600">{team.property_name}</td>
                            <td className="px-4 py-3 text-right text-[13px] font-semibold text-gray-900">{fmtDuration(team.total_duration_seconds ?? 0)}</td>
                            <td className="px-4 py-3 text-right text-[13px] text-gray-700">{(team.total_calls ?? 0).toLocaleString()}</td>
                            <td className="px-4 py-3 text-right text-[13px]">
                              <span className={completionPct(team.completed_calls ?? 0, team.total_calls ?? 0) >= '95%' ? 'text-green-600 font-medium' : 'text-amber-600'}>
                                {completionPct(team.completed_calls ?? 0, team.total_calls ?? 0)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right text-[13px] text-gray-500">{team.device_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
          </>
        </div>
      </div>

      {/* ── Incident Breakdown ── */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <ShieldAlert size={17} className="text-red-400" />
            <h2 className="text-[16px] font-bold text-gray-900">Incident Breakdown</h2>
            {incidents && incidents.total_incidents > 0 && (
              <span className="rounded-full bg-red-50 px-2.5 py-0.5 text-[12px] font-semibold text-red-600">{incidents.total_incidents} total</span>
            )}
          </div>
          {selectedOrgId && <span className="text-[12px] text-brand-green">(filtered by topbar)</span>}
        </div>
        <div className="p-6">
          {incidentsLoading ? (
            <p className="py-8 text-center text-[13px] text-gray-400">Loading incidents...</p>
          ) : !incidents || incidents.total_incidents === 0 ? (
            <p className="py-8 text-center text-[13px] text-gray-400">No incidents recorded for this period.</p>
          ) : (
            <div className="space-y-3">
              {(incidents.by_type ?? []).map((t) => (
                <div key={t.intent_type}>
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-[13px] text-gray-700">{t.label || t.intent_type.replace(/_/g, ' ')}</span>
                    <span className="text-[13px] font-semibold text-gray-900">
                      {t.count.toLocaleString()}
                      <span className="ml-1.5 text-[11px] font-normal text-gray-400">({t.percentage.toFixed(1)}%)</span>
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full rounded-full bg-red-400 transition-all" style={{ width: `${Math.min(t.percentage, 100)}%` }} />
                  </div>
                </div>
              ))}
              {(incidents.top_property || incidents.peak_hour !== null) && (
                <div className="flex flex-wrap gap-5 border-t border-gray-100 pt-3 text-[12px] text-gray-500">
                  {incidents.top_property && (
                    <span>Most active: <span className="font-medium text-gray-800">{incidents.top_property.property_name}</span> ({incidents.top_property.count} incidents)</span>
                  )}
                  {incidents.peak_hour !== null && (
                    <span>Peak hour: <span className="font-medium text-gray-800">{String(incidents.peak_hour).padStart(2, '0')}:00</span></span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Device Health ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wifi size={16} className="text-brand-green" />
              <h2 className="text-[15px] font-bold text-gray-900">Device Health</h2>
            </div>
            {deviceHealth && <span className="text-[13px] text-gray-500">{deviceHealth.total_devices} total</span>}
          </div>
          {deviceHealthLoading ? (
            <p className="py-8 text-center text-[13px] text-gray-400">Loading...</p>
          ) : !deviceHealth ? (
            <p className="py-8 text-center text-[13px] text-gray-400">Unavailable</p>
          ) : (
            <div className="space-y-3.5">
              {([
                ['Online',   deviceHealth.online_count,   'bg-green-500', 'bg-green-500'],
                ['Degraded', deviceHealth.degraded_count, 'bg-amber-400', 'bg-amber-400'],
                ['Offline',  deviceHealth.offline_count,  'bg-red-400',   'bg-red-400'],
                ['Blocked',  deviceHealth.blocked_count,  'bg-gray-400',  'bg-gray-400'],
                ['Unknown',  deviceHealth.unknown_count,  'bg-slate-300', 'bg-slate-300'],
              ] as [string, number, string, string][]).map(([label, count, dotCls, barCls]) => {
                const pct = deviceHealth.total_devices ? (count / deviceHealth.total_devices) * 100 : 0
                return (
                  <div key={label}>
                    <div className="mb-1 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${dotCls}`} />
                        <span className="text-[13px] text-gray-700">{label}</span>
                      </div>
                      <span className="text-[13px] font-semibold text-gray-900">
                        {count} <span className="text-[11px] font-normal text-gray-400">{pct.toFixed(0)}%</span>
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                      <div className={`h-full rounded-full ${barCls}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )
              })}
              {(deviceHealth.offline_count + deviceHealth.blocked_count) > 0 && (
                <p className="text-[12px] font-medium text-red-500">
                  {deviceHealth.offline_count + deviceHealth.blocked_count} device{deviceHealth.offline_count + deviceHealth.blocked_count !== 1 ? 's' : ''} need attention
                </p>
              )}
            </div>
          )}
        </div>


      {/* ── Monthly Consumption ── */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-6 py-4">
          <h2 className="text-[16px] font-bold text-gray-900">Consumption</h2>
          <div className="flex items-center gap-1">
            <button type="button"
              onClick={() => {
                const d = new Date(consumptionYear, consumptionMonth - 2)
                setConsumptionYear(d.getFullYear()); setConsumptionMonth(d.getMonth() + 1)
              }}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
              <ChevronLeft size={16} />
            </button>
            <span className="min-w-[120px] text-center text-[13px] font-medium text-gray-700">
              {consumption?.month_label ?? `${MONTH_NAMES[consumptionMonth - 1]} ${consumptionYear}`}
            </span>
            <button type="button"
              onClick={() => {
                const d = new Date(consumptionYear, consumptionMonth)
                setConsumptionYear(d.getFullYear()); setConsumptionMonth(d.getMonth() + 1)
              }}
              disabled={consumptionYear === new Date().getFullYear() && consumptionMonth === new Date().getMonth() + 1}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
        <div className="p-6">
          {consumptionLoading ? (
            <p className="py-8 text-center text-[13px] text-gray-400">Loading...</p>
          ) : !consumption ? (
            <p className="py-8 text-center text-[13px] text-gray-400">Unavailable</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500">Organisation</th>
                    <th className="px-4 py-2.5 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500">Type</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Transmissions</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">Audio Time</th>
                    <th className="px-4 py-2.5 text-right text-[11px] font-medium uppercase tracking-wide text-gray-500">vs Last Month</th>
                  </tr>
                </thead>
                <tbody>
                  {consumption.organizations.map((org) => {
                    const up = org.change_percent > 0
                    const noChange = org.change_percent === 0
                    return (
                      <tr key={org.organization_id} className="border-t border-gray-100 hover:bg-gray-50/40">
                        <td className="px-4 py-3 text-[13px] font-medium text-gray-900">{org.organization_name}</td>
                        <td className="px-4 py-3">
                          {org.organization_type ? (
                            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[11px] font-medium text-gray-600">
                              {org.organization_type}
                            </span>
                          ) : <span className="text-[12px] text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-[13px] text-gray-700">{org.total_transmissions.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right text-[13px] font-semibold text-gray-900">{fmtDuration(org.total_audio_seconds)}</td>
                        <td className="px-4 py-3 text-right text-[13px] font-medium">
                          {noChange ? <span className="text-gray-400">—</span> : (
                            <span className={up ? 'text-green-600' : 'text-red-500'}>
                              {up ? '+' : ''}{org.change_percent.toFixed(1)}% {up ? '↑' : '↓'}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td className="px-4 py-3 text-[13px] font-semibold text-gray-900" colSpan={2}>Platform Total</td>
                    <td className="px-4 py-3 text-right text-[13px] font-semibold text-gray-900">{consumption.platform_totals.total_transmissions.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-[13px] font-semibold text-gray-900">{fmtDuration(consumption.platform_totals.total_audio_seconds)}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
